'use client';

import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Minus, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import PrintRunningOrder from '@/components/PrintRunningOrder';
import { createClient } from '@supabase/supabase-js';
import Link from 'next/link';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

interface GuestEntry {
  name: string;
  dietary: string;
  dietaryOther: string;
}

interface RSVPFormValues {
  attending: 'yes' | 'no' | '';
  primaryName: string;
  primaryEmail: string;
  primaryDietary: string;
  primaryDietaryOther: string;
  message: string;
  volunteer: string;
  plusOnes: GuestEntry[];
  fridayEvening: boolean;
  sundayBrunch: boolean;
  beachSauna: boolean;
}

const dietaryOptions = [
  { value: 'none', label: 'No requirements' },
  { value: 'nut-allergy', label: 'Nut Allergy' },
  { value: 'gluten-free', label: 'Gluten Free' },
  { value: 'other', label: 'Other (please mention in message)' },
];

export default function RSVPForm({ defaultEmail, onSubmitSuccess }: { defaultEmail?: string; onSubmitSuccess?: () => void } = {}) {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAccountSection, setShowAccountSection] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState('');
  const [accountError, setAccountError] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<RSVPFormValues>({
    defaultValues: {
      attending: '',
      primaryName: '',
      primaryEmail: defaultEmail || '',
      primaryDietary: 'none',
      primaryDietaryOther: '',
      message: '',
      volunteer: '',
      plusOnes: [],
      fridayEvening: false,
      sundayBrunch: false,
      beachSauna: false,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'plusOnes' });

  const attending = watch('attending');
  const primaryDietary = watch('primaryDietary');

  // Backend integration point: POST /api/rsvp
  const onSubmit = async (data: RSVPFormValues) => {
    setIsLoading(true);
    setAccountError('');

    // Validate optional account creation
    if (showAccountSection && accountPassword) {
      if (accountPassword.length < 6) {
        setAccountError('Password must be at least 6 characters.');
        setIsLoading(false);
        return;
      }
      if (accountPassword !== accountPasswordConfirm) {
        setAccountError('Passwords do not match.');
        setIsLoading(false);
        return;
      }
    }

    try {
      // 1. Save RSVP to Supabase
      const { error: dbError } = await supabase.from('rsvp_submissions').insert({
        attending: data.attending,
        primary_name: data.primaryName,
        primary_email: data.primaryEmail || null,
        primary_dietary: data.primaryDietary,
        message: data.message || null,
        plus_ones: data.plusOnes,
        friday_evening: data.fridayEvening,
        sunday_brunch: data.sundayBrunch,
        sunday_walk: data.beachSauna,
      });

      if (dbError) {
        console.error('DB error:', dbError);
        throw new Error(dbError.message);
      }

      // 2. Send confirmation email if email provided (both attending and not attending)
      if (data.primaryEmail) {
        const { error: fnError } = await supabase.functions.invoke('send-rsvp-confirmation', {
          body: {
            attending: data.attending,
            primaryName: data.primaryName,
            primaryEmail: data.primaryEmail,
            primaryDietary: data.primaryDietary,
            plusOnes: data.plusOnes,
            fridayEvening: data.fridayEvening,
            sundayBrunch: data.sundayBrunch,
            sundayWalk: data.beachSauna,
            message: data.message,
          },
        });
        if (fnError) {
          console.error('Email send error:', fnError);
        }
      }

      // 3. Optionally create account
      if (showAccountSection && accountPassword && data.primaryEmail) {
        const { error: signUpError } = await supabase.auth.signUp({
          email: data.primaryEmail,
          password: accountPassword,
          options: {
            data: { full_name: data.primaryName },
          },
        });
        if (signUpError) {
          console.error('Account creation error:', signUpError);
          // Don't block RSVP submission — just note it
          toast.error('RSVP saved, but account creation failed: ' + signUpError.message);
        } else {
          setAccountCreated(true);
        }
      }

      toast.success('Your RSVP has been received. Thank you.');
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('RSVP submission error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-12 pb-28 px-8 md:px-16 xl:px-24">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle size={40} className="text-olive mx-auto mb-8" strokeWidth={1} />
          <h2 className="font-serif text-display-md text-faded-navy mb-6">
            {attending === 'yes' ? 'We cannot wait to celebrate with you.' : 'Thank you for letting us know.'}
          </h2>
          <div className="h-px bg-border w-16 mx-auto mb-6" />
          <p className="font-sans text-sm text-stone leading-relaxed">
            {attending === 'yes' ?'Your RSVP has been received. We will be in touch with further details as the day approaches. In the meantime, explore Dartmouth below.' :'We are sorry you will not be able to join us. You will be in our thoughts on the day.'}
          </p>
          <div className="mt-8">
            <Link
              href="/places-to-stay-things-to-do"
              className="inline-block font-sans text-sm text-olive underline underline-offset-4 hover:text-faded-navy transition-colors"
            >
              Accommodation &amp; Activities
            </Link>
          </div>
          {attending === 'yes' && (
            <div className="mt-10 flex justify-center">
              <PrintRunningOrder />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="pt-12 pb-24 px-8 md:px-16 xl:px-24">
      <div className="max-w-2xl mx-auto">
        {/* Section header */}
        <div className="mb-16">
          <p className="section-label mb-4">RSVP</p>
          <h2 className="font-serif text-display-md text-faded-navy mb-6">
            Will you join us?
          </h2>
          <p className="font-sans text-sm text-stone leading-relaxed">
            Please let us know if you are able to come, and share any dietary requirements so we can take care of you properly.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-14">
          {/* Attendance */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-6">
              Attendance
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'yes', label: 'Joyfully accepts', sublabel: 'I will be there' },
                { value: 'no', label: 'Regretfully declines', sublabel: 'I cannot attend' },
              ].map((option) => (
                <label
                  key={`attend-${option.value}`}
                  className={`relative flex flex-col items-center justify-center p-6 border cursor-pointer transition-all duration-300 ${
                    attending === option.value
                      ? 'border-faded-navy bg-off-white' :'border-border hover:border-warm-sand'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    className="sr-only"
                    {...register('attending', { required: 'Please let us know if you can attend' })}
                  />
                  <span className="font-serif text-lg text-faded-navy mb-1">{option.label}</span>
                  <span className="text-xs font-sans text-stone">{option.sublabel}</span>
                  {attending === option.value && (
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-faded-navy" />
                  )}
                </label>
              ))}
            </div>
            {errors.attending && (
              <p className="mt-3 text-xs font-sans text-red-500">{errors.attending.message}</p>
            )}
          </div>

          {/* Only show rest if attending */}
          {attending === 'yes' && (
            <>
              {/* Primary guest details */}
              <div className="space-y-8">
                <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">
                  Your Details
                </p>

                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    className="input-field"
                    {...register('primaryName', { required: 'Please enter your name' })}
                  />
                  {errors.primaryName && (
                    <p className="mt-2 text-xs font-sans text-red-500">{errors.primaryName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="input-field"
                    {...register('primaryEmail', {
                      required: 'Please enter your email',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email' },
                    })}
                  />
                  {errors.primaryEmail && (
                    <p className="mt-2 text-xs font-sans text-red-500">{errors.primaryEmail.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                    Dietary Requirements
                  </label>
                  <p className="text-xs font-sans text-stone/70 italic mb-2">
                    🌿 Please note: all catering on the day will be vegan.
                  </p>
                  <p className="text-xs font-sans text-stone/60 mb-3">
                    Please let us know of any dietary needs, allergies, or intolerances so we can ensure you are well catered for.
                  </p>
                  <select
                    className="input-field bg-transparent cursor-pointer"
                    {...register('primaryDietary')}
                  >
                    {dietaryOptions.map((opt) => (
                      <option key={`diet-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Plus ones */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">
                    Additional Guests
                  </p>
                  <button
                    type="button"
                    onClick={() => append({ name: '', dietary: 'none', dietaryOther: '' })}
                    className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-sans text-faded-navy hover:text-stone transition-colors duration-300"
                  >
                    <Plus size={14} />
                    Add Guest
                  </button>
                </div>

                {fields.length === 0 && (
                  <p className="text-xs font-sans text-stone/60 italic">
                    If you are bringing a plus-one or family members, add them here.
                  </p>
                )}

                {fields.map((field, index) => (
                  <div key={field.id} className="border border-border p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <p className="text-xs tracking-[0.15em] uppercase font-sans text-stone">
                        Guest {index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-stone/50 hover:text-red-400 transition-colors duration-200"
                        aria-label="Remove guest"
                      >
                        <Minus size={14} />
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                        Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="Guest full name"
                        className="input-field"
                        {...register(`plusOnes.${index}.name` as const, { required: 'Please enter the guest name' })}
                      />
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                        Dietary Requirements
                      </label>
                      <p className="text-xs font-sans text-stone/70 italic mb-2">
                        🌿 Please note: all catering on the day will be vegan.
                      </p>
                      <select
                        className="input-field bg-transparent cursor-pointer"
                        {...register(`plusOnes.${index}.dietary` as const)}
                      >
                        {dietaryOptions.map((opt) => (
                          <option key={`plus-diet-${index}-${opt.value}`} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Weekend events */}
              <div className="space-y-6">
                <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">
                  Weekend Events
                </p>
                <p className="text-xs font-sans text-stone/70 leading-relaxed">
                  We have a few other gatherings planned across the weekend. Please let us know if you plan to join, this helps us with numbers.
                </p>

                {[
                  {
                    field: 'fridayEvening' as const,
                    label: 'Friday 28th August, Evening',
                    note: 'Evening Apéritif at Galions Quay — 17:00 to 19:00',
                  },
                  {
                    field: 'sundayBrunch' as const,
                    label: 'Sunday 30th August, Morning on the Beach',
                    note: 'Blackpool Sands · 10:00 onwards',
                  },
                ].map((item) => (
                  <label
                    key={`event-${item.field}`}
                    className="flex items-start gap-4 cursor-pointer group"
                  >
                    <div className="relative mt-0.5">
                      <input
                        type="checkbox"
                        className="sr-only"
                        {...register(item.field)}
                      />
                      <div className={`w-4 h-4 border transition-all duration-300 flex items-center justify-center ${
                        watch(item.field) ? 'border-faded-navy bg-faded-navy' : 'border-border group-hover:border-warm-sand'
                      }`}>
                        {watch(item.field) && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="#FAF8F3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="font-sans text-sm text-faded-navy">{item.label}</p>
                      <p className="font-sans text-xs text-stone mt-0.5">{item.note}</p>
                    </div>
                  </label>
                ))}

                {/* Beach Sauna Sessions — shown below Sunday 30th */}
                <label className="flex items-start gap-4 cursor-pointer group pl-6">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      className="sr-only"
                      {...register('beachSauna')}
                    />
                    <div className={`w-4 h-4 border transition-all duration-300 flex items-center justify-center ${
                      watch('beachSauna') ? 'border-faded-navy bg-faded-navy' : 'border-border group-hover:border-warm-sand'
                    }`}>
                      {watch('beachSauna') && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#FAF8F3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="font-sans text-sm text-faded-navy">Interested in a Beach Sauna Session?</p>
                    <p className="font-sans text-xs text-stone mt-0.5">Blackpool Sands · details to follow</p>
                  </div>
                </label>
              </div>

              {/* Volunteer */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3 font-bold">
                  Would you like to volunteer?
                </label>
                <p className="text-xs font-sans text-stone/60 mb-3">
                  We'd love your help! Are you happy to volunteer for photography, vegan baking, or any other activities or talents you'd like to share?
                </p>
                <textarea
                  rows={3}
                  placeholder="e.g. I'd be happy to help with photography, or I can bake vegan treats..."
                  className="input-field resize-none"
                  {...register('volunteer')}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  A message for George & Boadecea
                </label>
                <p className="text-xs font-sans text-stone/60 mb-3">
                  Entirely optional — but always treasured.
                </p>
                <textarea
                  rows={4}
                  placeholder="Share your wishes, a memory, or simply say hello..."
                  className="input-field resize-none"
                  {...register('message')}
                />
              </div>
            </>
          )}

          {/* Not attending message field */}
          {attending === 'no' && (
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                Your Name
              </label>
              <input
                type="text"
                placeholder="Your full name"
                className="input-field mb-8"
                {...register('primaryName', { required: 'Please enter your name' })}
              />
              <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                Email Address
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                className="input-field mb-8"
                {...register('primaryEmail', {
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email' },
                })}
              />
              {errors.primaryEmail && (
                <p className="mt-2 mb-6 text-xs font-sans text-red-500">{errors.primaryEmail.message}</p>
              )}
              <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                A message for the couple
              </label>
              <textarea
                rows={4}
                placeholder="We will miss you — feel free to leave a message..."
                className="input-field resize-none"
                {...register('message')}
              />
            </div>
          )}

          {attending && (
            <>
              {/* Optional account creation */}
              <div className="border border-border">
                <button
                  type="button"
                  onClick={() => setShowAccountSection(!showAccountSection)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left group"
                >
                  <div>
                    <p className="font-sans text-sm text-faded-navy">
                      Create an account{' '}
                      <span className="text-xs text-stone/60 font-normal normal-case tracking-normal">— optional</span>
                    </p>
                    <p className="font-sans text-xs text-stone/60 mt-1">
                      Save your choices and return later to view your RSVP details
                    </p>
                  </div>
                  <div className="text-stone/50 group-hover:text-faded-navy transition-colors duration-200 ml-4 flex-shrink-0">
                    {showAccountSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>

                {showAccountSection && (
                  <div className="px-6 pb-6 space-y-5 border-t border-border pt-5">
                    <p className="font-sans text-xs text-stone/70 leading-relaxed">
                      This is entirely optional. If you create an account using the email above, you can sign in to the Guest Dashboard at any time to review your RSVP.
                    </p>

                    {accountError && (
                      <div className="p-3 border border-red-200 bg-red-50 text-xs font-sans text-red-600">
                        {accountError}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                        Choose a Password
                      </label>
                      <input
                        type="password"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        className="input-field"
                        minLength={6}
                      />
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={accountPasswordConfirm}
                        onChange={(e) => setAccountPasswordConfirm(e.target.value)}
                        placeholder="Repeat your password"
                        className="input-field"
                        minLength={6}
                      />
                    </div>

                    <p className="font-sans text-xs text-stone/50 italic">
                      Your account will use the email address entered above. You can sign in at{' '}
                      <Link href="/guest-dashboard" className="underline underline-offset-2 hover:text-faded-navy transition-colors">
                        the Guest Dashboard
                      </Link>{' '}
                      after submitting.
                    </p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-60 flex items-center justify-center gap-3"
                style={{ minHeight: '52px' }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending
                  </>
                ) : (
                  'Send RSVP'
                )}
              </button>
            </>
          )}
        </form>
        {/* Already RSVPd link */}
        <div className="mt-8 pt-6 border-t border-border flex items-center justify-center gap-3">
          <p className="font-sans text-xs text-stone/70">Already submitted your RSVP?</p>
          <Link
            href="/guest-dashboard"
            className="inline-flex items-center gap-1.5 font-sans text-xs tracking-[0.15em] uppercase text-faded-navy hover:text-deep-navy transition-colors duration-300 underline underline-offset-4"
          >
            Enter Guest Dashboard
          </Link>
        </div>
      </div>
    </section>
  );
}